import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Inbox,
  Loader2,
  MessageSquareText,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  usePatientDoctorMessageThreads,
  useDoctorThreadByLabVisit,
  useReplyDoctorMessageThread,
  useSetDoctorThreadStatus,
  useDoctorReplySuggestion,
  useRequestDoctorReplySuggestion,
  type DoctorMessageThreadRow,
} from "@/hooks/api";
import { cn } from "@/lib/cn";
import { ReadState } from "@/components/doctor-messages/read-state";
import { toast } from "sonner";

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Doctor-message threads for one customer on the user-detail page.
 * Shows ALL of the user's threads across lab visits — the thread for the
 * currently selected visit is pinned first and expanded, so the doctor finds
 * the conversation for the lab visit in question without hunting.
 */
export function DoctorMessagesPanel({
  authUserId,
  selectedLabVisitId,
  autoScroll,
}: {
  authUserId: string;
  selectedLabVisitId: string | null;
  /** Deep link from the kundebehandling Messages tab: scroll here once loaded. */
  autoScroll?: boolean;
}) {
  const { data: threads, isLoading } = usePatientDoctorMessageThreads(authUserId);

  useEffect(() => {
    if (!autoScroll || isLoading || !threads || threads.length === 0) return;
    document
      .getElementById("meldinger")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [autoScroll, isLoading, threads]);

  const ordered = useMemo(() => {
    const list = [...(threads ?? [])];
    // Selected visit's thread first, then by last activity (server order).
    if (selectedLabVisitId) {
      list.sort(
        (a, b) =>
          (b.labVisitId === selectedLabVisitId ? 1 : 0) -
          (a.labVisitId === selectedLabVisitId ? 1 : 0),
      );
    }
    return list;
  }, [threads, selectedLabVisitId]);

  if (isLoading) return null;
  // Nothing to answer until the customer has started a conversation.
  if (!threads || threads.length === 0) return null;

  return (
    <Card id="meldinger">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-admin-primary" />
          <h3 className="text-sm font-semibold">Pasientdialog</h3>
          <span className="text-xs text-admin-text-subtle">
            {threads.length} dialog{threads.length === 1 ? "" : "er"}
          </span>
        </div>
        <div className="space-y-2">
          {ordered.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isCurrentVisit={thread.labVisitId === selectedLabVisitId}
              defaultOpen={
                thread.labVisitId === selectedLabVisitId &&
                thread.status === "open"
              }
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ThreadItem({
  thread,
  isCurrentVisit,
  defaultOpen,
}: {
  thread: DoctorMessageThreadRow;
  isCurrentVisit: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { data: detail, isLoading: detailLoading } =
    useDoctorThreadByLabVisit(open ? thread.labVisitId : null);

  return (
    <div
      className={cn(
        "rounded-md border",
        isCurrentVisit
          ? "border-admin-primary/60"
          : "border-admin-card-border",
        thread.status === "open" && "bg-admin-card/50",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-admin-text-subtle" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-admin-text-subtle" />
        )}
        <span className="text-xs text-admin-text-secondary whitespace-nowrap">
          Besøk {thread.labVisitDate
            ? new Date(thread.labVisitDate).toLocaleDateString("nb-NO", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "–"}
        </span>
        {thread.status === "open" ? (
          <Badge className="bg-admin-success-400/20 text-admin-success-400 border-0 text-[10px]">
            Åpen
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">
            Lukket
          </Badge>
        )}
        <ReadState
          hasUnread={thread.hasUnread}
          userReadAt={thread.userReadAt}
          className="text-[11px]"
        />
        <span className="ml-auto text-[11px] text-admin-text-subtle whitespace-nowrap">
          {thread.messageCount} meldinger
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {detailLoading || !detail ? (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {detail.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
                      m.senderKind === "doctor"
                        ? "bg-admin-primary/10 ml-6"
                        : "bg-admin-card mr-6",
                    )}
                  >
                    <div className="flex items-center gap-2 text-[10px] text-admin-text-subtle mb-0.5">
                      <MessageSquareText className="h-3 w-3" />
                      {m.senderKind === "doctor"
                        ? m.senderName ?? "Lege"
                        : "Kunde"}
                      <span>·</span>
                      {formatDateTime(m.createdAt)}
                    </div>
                    {m.text}
                  </div>
                ))}
              </div>
              {detail.thread.status === "open" ? (
                <ReplyComposer threadId={thread.id} />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-admin-text-subtle">
                    {thread.closedAt
                      ? `Dialogen er lukket ${formatDateTime(thread.closedAt)}.`
                      : "Dialogen er lukket."}
                  </p>
                  <ThreadStatusButton threadId={thread.id} status="closed" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReplyComposer({ threadId }: { threadId: string }) {
  const [text, setText] = useState("");
  /** The AI draft in the box, as inserted, so an edited box is distinguishable
   *  from an untouched one. */
  const [draft, setDraft] = useState<string | null>(null);
  /** The customer message the draft in the box answers. */
  const [draftFrom, setDraftFrom] = useState<string | null>(null);
  /** A ready draft we did not insert because the doctor had written. */
  const [offer, setOffer] = useState<{
    draft: string;
    sourceMessageId: string;
  } | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  /** The doctor has put the cursor in the box since the draft landed. Until
   *  then the AI text cannot be sent. */
  const [reviewed, setReviewed] = useState(false);
  const latestText = useRef("");
  /** Bumped on every keystroke, so a response can tell whether the box was
   *  edited while the model worked. */
  const editRevision = useRef(0);
  /** The text and revision at the moment a generation was requested. */
  const requestText = useRef<string | null>(null);
  const requestRevision = useRef(0);
  /** The customer message the last sent reply answered: a cached or late
   *  draft for it must not drop into the now-empty box. */
  const answered = useRef<string | null>(null);
  const autoRequested = useRef(false);

  const reply = useReplyDoctorMessageThread();
  const {
    data: state,
    isLoading,
    isFetchedAfterMount,
  } = useDoctorReplySuggestion(threadId);
  const requestSuggestion = useRequestDoctorReplySuggestion();

  const ready = state?.status === "ready" ? (state.suggestion ?? null) : null;
  const readyDraft = ready?.draft ?? null;
  const readyFrom = ready?.sourceMessageId ?? null;
  const pending = state?.status === "pending" || requestSuggestion.isPending;
  const answerable = state?.answerable === true;
  const suggested = draft !== null;
  const aiText = suggested && text.trim() !== "";
  const awaitingReview = aiText && !reviewed;

  const editText = (value: string) => {
    latestText.current = value;
    setText(value);
  };

  const editFromUser = (value: string) => {
    editRevision.current += 1;
    editText(value);
  };

  // The draft is normally generated before the thread is opened, so it lands
  // in an empty box immediately. Insert only when the box is still empty or
  // still holds exactly what was there when generation started: anything the
  // doctor typed during the wait wins, and the draft is offered instead.
  // Cached state from before this mount may answer an older question, so
  // nothing is inserted until it has been refetched.
  useEffect(() => {
    if (!isFetchedAfterMount || !readyDraft || !readyFrom) return;
    if (readyFrom === answered.current) return;
    if (readyDraft === draft && readyFrom === draftFrom) return;
    const unchangedSinceRequest =
      requestRevision.current === editRevision.current &&
      latestText.current === (requestText.current ?? "");
    requestText.current = null;
    if (latestText.current.trim() === "" || unchangedSinceRequest) {
      editText(readyDraft);
      setDraft(readyDraft);
      setDraftFrom(readyFrom);
      setReviewed(false);
      setOffer(null);
    } else {
      setOffer({ draft: readyDraft, sourceMessageId: readyFrom });
    }
  }, [readyDraft, readyFrom, draft, draftFrom, isFetchedAfterMount]);

  // A draft answers one customer message. Once the thread has moved past it
  // (a new question, a reply sent elsewhere), its offer and an untouched copy
  // in the box go; text the doctor edited stays.
  useEffect(() => {
    if (!state) return;
    const current = state.answerable
      ? state.suggestion?.sourceMessageId
      : undefined;
    if (offer && offer.sourceMessageId !== current) setOffer(null);
    if (draftFrom && draftFrom !== current && latestText.current === draft) {
      editText("");
      setDraft(null);
      setDraftFrom(null);
    }
  }, [state, offer, draft, draftFrom]);

  // Threads from before eager generation, or whose job failed, get one
  // automatic request. The button stays for retries.
  useEffect(() => {
    if (autoRequested.current) return;
    if (state?.status !== "none" || !state.answerable) return;
    autoRequested.current = true;
    requestSuggestion.mutate(threadId);
  }, [state?.status, state?.answerable, threadId]);

  const send = (close: boolean) => {
    const body = text.trim();
    if (!body || reply.isPending || awaitingReview) return;
    const question = state?.suggestion?.sourceMessageId ?? draftFrom;
    reply.mutate(
      { threadId, text: body, close },
      {
        onSuccess: () => {
          answered.current = question;
          requestText.current = null;
          editText("");
          setDraft(null);
          setDraftFrom(null);
          setOffer(null);
          toast.success(close ? "Svar sendt og dialogen lukket" : "Svar sendt");
        },
        onError: () => toast.error("Kunne ikke sende svaret"),
      },
    );
  };

  const requestFresh = () => {
    setConfirmReplace(false);
    // What the box holds now is what the doctor consented to replace; an edit
    // made while the model works must survive and turn the draft into an offer.
    requestText.current = latestText.current;
    requestRevision.current = editRevision.current;
    requestSuggestion.mutate(threadId, {
      onError: (error) => {
        requestText.current = null;
        toast.error(error.message);
      },
    });
  };

  const onSuggest = () => {
    // Never throw away text the doctor typed without asking.
    if (text.trim().length > 0 && text !== draft) {
      setConfirmReplace(true);
      return;
    }
    requestFresh();
  };

  const useOffer = () => {
    if (!offer) return;
    editRevision.current += 1;
    editText(offer.draft);
    setDraft(offer.draft);
    setDraftFrom(offer.sourceMessageId);
    setReviewed(false);
    setOffer(null);
  };

  // A draft can land while the cursor is already in the box, so a click or
  // key press counts as well as focus.
  const markReviewed = () => setReviewed(true);

  return (
    <div className="space-y-2">
      {aiText && (
        <div className="flex items-center gap-1.5 text-xs">
          <Badge className="shrink-0 gap-1 whitespace-nowrap border-0 bg-admin-primary/15 text-admin-primary text-[10px]">
            <Sparkles className="h-3 w-3" />
            AI-utkast
          </Badge>
          <span className="text-admin-text-subtle">
            Laget ut fra pasientdataene og tidligere svar. Les gjennom og
            rediger før du sender.
          </span>
        </div>
      )}
      <Textarea
        value={text}
        onChange={(e) => editFromUser(e.target.value)}
        onFocus={markReviewed}
        onPointerDown={markReviewed}
        onKeyDown={markReviewed}
        placeholder="Svar til kunden…"
        rows={suggested ? 5 : 3}
        className={cn(
          aiText && "border-admin-primary/60 bg-admin-primary/5",
          awaitingReview && "text-admin-text-secondary",
        )}
      />
      {awaitingReview && (
        <p className="text-[11px] text-admin-text-subtle">
          Klikk i feltet og les gjennom utkastet før du kan sende.
        </p>
      )}
      {offer && offer.draft !== text && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-admin-primary/40 bg-admin-primary/5 px-2 py-1.5">
          <Sparkles className="h-3.5 w-3.5 text-admin-primary" />
          <span className="text-xs text-admin-text-subtle">
            Et AI-forslag er klart.
          </span>
          <Button size="sm" variant="outline" onClick={useOffer}>
            Bruk forslaget
          </Button>
        </div>
      )}
      {confirmReplace && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-admin-card-border px-2 py-1.5">
          <span className="text-xs text-admin-text-subtle">
            Feltet har allerede tekst. Erstatte den med et forslag?
          </span>
          <Button size="sm" variant="outline" onClick={requestFresh}>
            Erstatt
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmReplace(false)}
          >
            Avbryt
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!text.trim() || reply.isPending || awaitingReview}
          onClick={() => send(false)}
        >
          Svar
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!text.trim() || reply.isPending || awaitingReview}
          onClick={() => send(true)}
        >
          Svar og lukk
        </Button>
        {answerable && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={pending || isLoading}
            onClick={onSuggest}
            title="Lag et utkast fra pasientens spørsmål. Du kan redigere det før du sender."
          >
            {pending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="mr-1 h-3.5 w-3.5" />
            )}
            {pending
              ? "Lager forslag…"
              : draft || offer
                ? "Nytt forslag"
                : "Foreslå svar"}
          </Button>
        )}
        <ThreadStatusButton
          threadId={threadId}
          status="open"
          className={answerable ? "ml-0" : "ml-auto"}
        />
      </div>
    </div>
  );
}

/** "Lukk uten svar" on an open thread, "Gjenåpne" on a closed one. Neither
 *  sends the customer a message or a push. */
function ThreadStatusButton({
  threadId,
  status,
  className,
}: {
  threadId: string;
  status: "open" | "closed";
  className?: string;
}) {
  const setStatus = useSetDoctorThreadStatus();
  const next = status === "open" ? "closed" : "open";

  return (
    <Button
      size="sm"
      variant="ghost"
      className={className}
      disabled={setStatus.isPending}
      onClick={() =>
        setStatus.mutate(
          { threadId, status: next },
          {
            onSuccess: () =>
              toast.success(
                next === "closed" ? "Dialogen er lukket" : "Dialogen er gjenåpnet",
              ),
            onError: () => toast.error("Kunne ikke endre dialogen"),
          },
        )
      }
    >
      {next === "closed" ? "Lukk uten svar" : "Gjenåpne"}
    </Button>
  );
}
